/**
 * /hero-samples — internal preview page (iter 110).
 *
 * Shows all 4 hero-illustration candidates side-by-side on the navy hero
 * background, so the user can compare and pick one. NOT linked from the
 * sidebar — only reachable via direct URL.
 */
import React from 'react';
import HeroSampleB from '../components/hero/HeroSampleB';
import HeroSampleC from '../components/hero/HeroSampleC';

function Frame({ tag, title, blurb, children }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow-2xl border border-[#003D80]/30" data-testid={`hero-sample-frame-${tag.toLowerCase()}`}>
      <div className="bg-[#003D80] px-5 py-3 flex items-center justify-between text-white">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F2A100] font-bold">Option {tag}</div>
          <div className="font-heading font-bold text-lg">{title}</div>
        </div>
        <div className="text-xs text-[#F2EBDB]/70 text-right max-w-xs hidden md:block">{blurb}</div>
      </div>
      <div className="bg-[#003D80] aspect-[4/3] flex items-center justify-center p-4">
        {children}
      </div>
      <div className="md:hidden bg-[#003D80] px-5 py-3 text-xs text-[#F2EBDB]/70">{blurb}</div>
    </div>
  );
}

export default function HeroSamples() {
  return (
    <div className="min-h-screen bg-[#F2EBDB] p-6 md:p-10">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#F2A100] font-bold mb-2">PREVIEW</div>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-[#003D80]">Hero illustration — pick one</h1>
          <p className="text-[#003D80]/70 mt-2 max-w-3xl">
            All 4 candidates rendered on the same navy hero background.
            Tell me the letter (A · B · C · D) and I&apos;ll wire it into the live landing page.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-6">
          <Frame
            tag="A"
            title="Isometric 3D-flat (Stripe / Linear)"
            blurb="AI-generated illustration. Therapist + patient + floating UI panels in flat-shaded 3D."
          >
            <img src="/hero-sample-a-isometric.png?v=1"
                 alt="Isometric 3D-flat sample"
                 className="max-w-full max-h-full object-contain"
                 data-testid="hero-sample-a-img"/>
          </Frame>

          <Frame
            tag="B"
            title="Outlined anatomy + data nodes"
            blurb="Pure inline SVG. Hand-drawn body silhouette with pulsing anatomical nodes wired to a live patient-chart card. Animated."
          >
            <div className="w-full max-w-md aspect-square">
              <HeroSampleB/>
            </div>
          </Frame>

          <Frame
            tag="C"
            title="Geometric abstract + floating feature cards"
            blurb="Pure inline SVG. Bold gold disc + stick-figure in motion + 4 drifting feature cards (AI Scribe / ROM / GDPR / Calendar). Fully animated."
          >
            <div className="w-full max-w-md aspect-square">
              <HeroSampleC/>
            </div>
          </Frame>

          <Frame
            tag="D"
            title="AI-generated vector/sticker (Notion / Webflow)"
            blurb="AI-generated illustration. Bold-outline cartoon therapist guiding a patient through movement with a holographic chart between them."
          >
            <img src="/hero-sample-d-vector.png?v=1"
                 alt="Vector / sticker sample"
                 className="max-w-full max-h-full object-contain"
                 data-testid="hero-sample-d-img"/>
          </Frame>
        </div>

        <footer className="mt-10 text-sm text-[#003D80]/70 text-center">
          Once you pick one, I&apos;ll drop it into <code className="bg-[#003D80]/10 px-1.5 py-0.5 rounded text-xs">Landing.jsx</code> in place of the current photo pair.
          The parallax + hover micro-interactions will carry over.
        </footer>
      </div>
    </div>
  );
}
